/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { isolateComponent } from '@zapier/style-encapsulation';
import { Colors, Link, Typography } from '@zapier/design-system';
import {
  FacebookIcon,
  LinkedInIcon,
  TwitterIcon,
  YouTubeIcon,
  RssIcon,
} from './icons';
import type { SocialIconType } from './icons';

type Props = {
  /**
   * Text to render before the icons.
   */
  title: string;
  /**
   * The social icons to render.
   */
  icons: Array<SocialIconType>;
};

const Styles = {
  root: css`
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  `,
  cell: css`
    flex-grow: 1;
    flex-shrink: 0;
    text-align: center;
  `,
  title: css`
    ${Typography.altText900};
    color: ${Colors.grey900};
    white-space: nowrap;
  `,
  icons: css`
    display: flex;
    width: min-content;
    margin: 0 auto;
  `,
  iconItem: css`
    margin-right: 10px;
    &:last-of-type {
      margin-right: 0;
    }
  `,
  icon: (icon: SocialIconType) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 100%;
    background-color: ${Colors.grey500};

    &:hover,
    &:focus {
      background-color: ${icon.color};
    }
  `,
  iconSvg: css`
    width: 16px;
    height: 16px;
    fill: ${Colors.white};

    > svg {
      width: inherit;
      height: inherit;
    }
  `,
};

const SocialIcon = (props: SocialIconType) => {
  return (
    <Link
      ariaLabel={props.title}
      title={props.title}
      color={null}
      css={Styles.icon(props)}
      data-testid={props.title}
      href={props.href}
    >
      <span
        css={Styles.iconSvg}
        dangerouslySetInnerHTML={{ __html: props.svg }}
      />
    </Link>
  );
};

/**
 * @private
 * Social icons that link to our social media accounts.
 */
export const SocialIcons = (props: Props) => {
  const iconNodes = props.icons.map((icon: SocialIconType) => {
    return (
      <li key={icon.title} css={Styles.iconItem}>
        <SocialIcon {...icon} />
      </li>
    );
  });
  return (
    <div css={Styles.root}>
      <div css={Styles.cell}>
        {/* `h6` ensures screenreaders can jump to it */}
        <h6 css={Styles.title}>{props.title}</h6>
      </div>
      <div css={Styles.cell}>
        <ul css={Styles.icons}>{iconNodes}</ul>
      </div>
    </div>
  );
};

SocialIcons.defaultProps = {
  title: 'Follow us',
  icons: [FacebookIcon, LinkedInIcon, TwitterIcon, YouTubeIcon, RssIcon],
};
